// src/lib/context.tsx — operator identity, toast, and the cached service list.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { ops, type Role, type ServiceList } from "./ops";

// ── Operator ────────────────────────────────────────────────
export interface Operator {
  email: string | null;
  role: Role;
}
const OperatorCtx = createContext<Operator | null>(null);
export const OperatorProvider = OperatorCtx.Provider;
export function useOperator(): Operator {
  const o = useContext(OperatorCtx);
  if (!o) throw new Error("useOperator outside OperatorProvider");
  return o;
}

// ── Toast (one at a time, center of the bottom third, no actions) ──
const ToastCtx = createContext<(msg: string) => void>(() => undefined);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const show = useCallback((m: string) => {
    window.clearTimeout(timer.current);
    setMsg(m);
    timer.current = window.setTimeout(() => setMsg(null), 3000);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div className="toast-slot" aria-live="polite">
        {msg && <div className="toast">{msg}</div>}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

// ── Services (ops_list_services, shared by every screen) ──
interface ServicesState {
  data: ServiceList | null;
  error: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
}
const ServicesCtx = createContext<ServicesState | null>(null);
export function ServicesProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<ServiceList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setData(await ops.listServices());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  return <ServicesCtx.Provider value={{ data, error, loading, refresh }}>{children}</ServicesCtx.Provider>;
}
export function useServices(): ServicesState {
  const s = useContext(ServicesCtx);
  if (!s) throw new Error("useServices outside ServicesProvider");
  return s;
}
